import React, { useState } from 'react'
import { Button } from 'components/ui/button'
import { cn } from 'lib/utils'
import type { StatementLayout } from '../constants/lineItems'

export interface EmptyStateProps {
	onUpload: () => void
	onCreateManually: (layout: StatementLayout) => void
}

const LAYOUTS: Array<{ value: StatementLayout; label: string; hint: string }> = [
	{ value: 'operator', label: 'Retail operator', hint: 'Sales, COGS, Gross Profit, Expenses, Earnings' },
	{ value: 'owner', label: 'Property owner', hint: 'Rental Revenue, Vacancy, EGI, Operating Expenses, NOI' },
]

export const EmptyState: React.FC<EmptyStateProps> = ({ onUpload, onCreateManually }) => {
	const [layout, setLayout] = useState<StatementLayout>('operator')

	return (
		<div className="flex flex-col items-center justify-center py-12 px-4">
			{/* Icon */}
			<div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mb-6">
				<svg
					className="w-12 h-12 text-blue-500"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
					/>
				</svg>
			</div>

			{/* Title */}
			<h3 className="text-xl font-semibold text-gray-900 mb-2">No Financial Data Yet</h3>

			{/* Description */}
			<p className="text-gray-600 text-center mb-8 max-w-md">
				Get started by uploading an existing income statement or manually entering your data below.
			</p>

			{/* Layout, for a statement entered by hand */}
			<div role="radiogroup" aria-label="Statement layout" className="mb-6 grid w-full max-w-md grid-cols-2 gap-2">
				{LAYOUTS.map(option => (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={layout === option.value}
						onClick={() => setLayout(option.value)}
						className={cn(
							'rounded-lg border p-3 text-left transition-colors',
							layout === option.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
						)}
					>
						<span className="block text-sm font-medium text-foreground">{option.label}</span>
						<span className="mt-1 block text-xs text-muted-foreground">{option.hint}</span>
					</button>
				))}
			</div>

			{/* Actions */}
			<div className="flex space-x-4">
				<Button
					variant="primary"
					onClick={onUpload}
				>
					<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
						/>
					</svg>
					Upload File
				</Button>

				<Button
					variant="ghost"
					onClick={() => onCreateManually(layout)}
				>
					<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
						/>
					</svg>
					Enter Manually
				</Button>
			</div>

			{/* Helper Text */}
			<div className="mt-8 p-4 bg-gray-50 rounded-lg max-w-md">
				<h4 className="text-sm font-medium text-gray-900 mb-2">💡 Quick Tip</h4>
				<p className="text-sm text-gray-600">
					You can upload income statements in <strong>.xlsx</strong> or <strong>.csv</strong>{' '}
					format. Make sure your file has quarters as columns and line items as rows.
				</p>
			</div>
		</div>
	)
}
